// SingleRiskReport.jsx
import React, { useState, useEffect } from 'react';
import { authFetch } from './utils/authFetch';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  TableContainer,
  TableCell,
  Table,
  TableBody, 
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
// Color maps for risk chips
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import RiskEvaluationForm from './RiskEvaluationForm';
import ControlsList from './ControlsList';
import ResidualRisk from './ResidualRisk';
import useSaveOperation from './useSaveOperation';
import { discardRisk, updateSavedRisk } from './redux/actions/riskActions';
import AutosaveIndicator from './AutoSaveIndicator';

const riskPriorityColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB',
};

const residualRiskColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB',
};

const actionPlanColors = {
  Unsatisfactory: '#D32F2F',
  Poor: '#FF5722',
  Fair: '#FFC107',
  Good: '#8BC34A',
  'Very Good': '#66BB6A',
  Excellent: '#4CAF50',
  None: '#EBEBEB',
};

const SingleRiskReport = ({
  risk,
  sectionName,
  sectionState,
  category,
  modelId,
  assessmentUuid,
  user,
  onClose,
}) => {
  const [editedRiskName, setEditedRiskName] = useState(risk.riskName || risk.name);
  const [isEditingRiskName, setIsEditingRiskName] = useState(false);
  const [detailTab, setDetailTab] = useState(0);  // 0 = Eval+Priority, 1 = Action Plan
  const { isSaving, isSaved, handleSaveOperation } = useSaveOperation();
   const dispatch = useDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  

  const updatedRisk = useSelector((state) => {
    const model = state.risks.byAssessment[modelId];
    if (!model) return risk;
    const section = model.modelRisks?.[sectionName];
    if (!section) return risk;
    const categoryRisks = section.areaRisks?.[category];
    if (!categoryRisks) return risk;
    const foundRisk = categoryRisks.find(r => r.riskId === risk.riskId);
    return foundRisk || risk;
  });

  // residual & action‑plan data for chips
  const residualRiskEntry = useSelector(
    (state) => state.residualRisk.byModel[modelId]?.[risk.riskId] || {}
  );

  const finalResidual = (residualRiskEntry.overridden?.residual_rating ??
                          residualRiskEntry.residual_rating) || 'N/A';
    const finalActionPlan = (residualRiskEntry.overridden?.action_plan_rating ??
                           residualRiskEntry.action_plan_rating) || 'N/A';

  const cap = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
 

  useEffect(() => {
    // Update local state when risk is updated in Redux
    setEditedRiskName(updatedRisk.riskName || updatedRisk.name);
    // setSelectedPriority(updatedRisk.priority || '');
    // setNewRationale(updatedRisk.rationale || '');
  }, [updatedRisk]);

  const handleSaveRisk = async () => {
    try {
      await handleSaveOperation(async () => {
        const ownerInfo = {
          name: user.name,
          email: user.email,
          picture: user.picture,
        };
        const response = await authFetch('/.netlify/functions/saveRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            riskId: updatedRisk.riskId, // Include riskId here
            name: editedRiskName,
            section: sectionName,
            category: category,
            modelUuid: modelId,
            assessmentUuid: assessmentUuid,
            owner: ownerInfo,
          }),
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save risk');
        }
  
        //const savedRisk = await response.json();
  
        // Update risk in Redux store
        dispatch(
          updateSavedRisk(modelId, updatedRisk.riskId, sectionName, category, {
            ...updatedRisk,
            riskName: editedRiskName,
            owner: ownerInfo,
          })
        );
  
        setSnackbarMessage('Risk saved successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
  
        // Set editing state back to false
        setIsEditingRiskName(false); // Ensure the icon changes back to Edit
      });
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDiscardRisk = () => {
    // Discard the risk
    dispatch(discardRisk(risk, modelId, sectionName, category));
    onClose(); // Close the editor
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3, position: 'relative' }}>

      {/* Top‑right close */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', top:12, right: 12, mb:2}}
      > <CloseIcon />
      </IconButton>

<TableContainer component={Box}>
  <Table size="small">
    <TableHead sx={{ backgroundColor: '#f4f4f4' }}>
      <TableRow>
      <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Risk Name</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>Residual Risk</TableCell>
        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Action Plan Analysis</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }} align="right">
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        {/* Risk name + chips */}
        <TableCell sx={{ border: 0, pl: 0 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {isEditingRiskName ? (
              <TextField
                label="Risk Name"
                value={editedRiskName}
                onChange={(e) => setEditedRiskName(e.target.value)}
                fullWidth
                size="small"
              />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {editedRiskName}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ border: 0 }}>
          {updatedRisk.priority && (
            <Chip
              size="small"
              label={cap(updatedRisk.priority)}
              sx={{
                backgroundColor:
                  riskPriorityColors[cap(updatedRisk.priority)] || '#EBEBEB',
                color: '#fff',
              }}
            />
          )}
        </TableCell>
        <TableCell sx={{ border: 0 }}>
          {finalResidual && finalResidual !== 'N/A' && (
            <Chip
              size="small"
              label={finalResidual}
              sx={{
                backgroundColor:
                  residualRiskColors[cap(finalResidual)] || '#EBEBEB',
                color: '#fff',
              }}
            />
          )}
        </TableCell>
        <TableCell sx={{ border: 0 }}>
          {finalActionPlan && finalActionPlan !== 'N/A' && (
            <Chip
              size="small"
              label={finalActionPlan}
              sx={{
                backgroundColor:
                  actionPlanColors[finalActionPlan] || '#EBEBEB',
                color: '#fff',
              }}
            />
          )}
        </TableCell>
        {/* actions */}
        <TableCell align="right" sx={{ border: 0, pr: 0, whiteSpace: 'nowrap' }}>
          {isEditingRiskName ? (
            <IconButton onClick={handleSaveRisk} color="primary">
              <CheckIcon />
            </IconButton>
          ) : (
            <IconButton onClick={() => setIsEditingRiskName(true)} color="primary">
              <EditIcon />
            </IconButton>
          )}
          <IconButton onClick={handleDiscardRisk} color="secondary">
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</TableContainer>
      <Divider sx={{ my: 2 }} />

      {/* Evaluation / Action Plan Tabs */}
      <Tabs
        value={detailTab}
        onChange={(e, v) => setDetailTab(v)}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2 }}
      >
        <Tab label="Evaluation & Priority" />
        <Tab
          label={
            <Box display="flex" alignItems="center" gap={1}>
              Action Plan
              <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
            </Box>
          }/>
        <Tab label="Residual Risk" />
      </Tabs>

      {detailTab === 0 && (
        <Box>
 

          {/* Risk Evaluation Form */}
          <RiskEvaluationForm
            modelId={modelId}
            riskName={editedRiskName}
            riskId={risk.riskId}
            assessmentUuid={assessmentUuid}
            sectionName={sectionName}
            category={category}
            handleSaveOperation={handleSaveOperation}
            hideTitle 
          />
        </Box>
      )}

      {detailTab === 1 && (
        <ControlsList
          risk={updatedRisk}
          modelId={modelId}
          assessmentUuid={assessmentUuid}
          handleSaveOperation={handleSaveOperation}
          category={category}
          sectionName={sectionName}
          sectionState={sectionState}
        />
      )}

      {detailTab === 2 && (
        <ResidualRisk
          modelId={modelId}
          assessmentUuid={assessmentUuid}
          sectionName={sectionName}
          riskId={risk.riskId}
          hideTitle
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Paper>

      
  );

};

export default SingleRiskReport;
